import React from 'react';
import { ColumnLayout, } from 'AWS-UI-Components-React';
import { AccuracyButtonNewStyle } from '../../../styles/common_styles';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { TrendReportChartTitle } from './trend/trend_report_chart_title';
import { TrendReportChart } from './trend/trend_report_chart';
import { GOAL_KEY, COMPONENT_GRAPH_ACCURACY_OPTION_KEYS, CHART_TYPE, MARKETPLACE, DOWNLOAD_FILE_NAME } from '../../../constants/trend_report';
import { replaceLiveSnapshotId, trendDataWithCompositeKeys, prepareSnapshotName } from '../utils/transformations';
import { getCompositeKeys, extractFromCompositeKey, searchCompositeKey, extractFromCompositeKeys } from '../utils/composite_keys';
import { SNAPSHOT_ID, KEY_SEPARATOR, OPERATION_STATUS } from '../common/constants';
import { getNonAggregatedFieldsWithMultiValues } from '../../../utils/payload';
import DownloadReportDataWindow from '../components/download_report_data_window';
import { REPORT_REQUEST_PAYLOAD } from '../../../constants/payload';

export class TrendComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      displayedChart: CHART_TYPE.LINE,
    };
    this.reportData = null;
    this.accuracyOptions = [];
    this.channelOptions = [];
    this.componentOptions = [];
    this.snapshotOptions = [];
    this.aggregatedOptions = [];
    this.data = {};
    this.allLinesId = [];

  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.downloadStatus !== this.props.downloadStatus && this.props.downloadStatus === OPERATION_STATUS.IN_PROGRESS) {
      this.beginDownloadData();
    }
  }

  beginDownloadData = () => {
    if (this.reportData.aggregatedFields.length > 0) {
      this.setState({ downloadDataWindowVisible: true });
    } else {
      this.onDownloadData();
    }
  };

  formatChartLabel = (key) => {
    const [compositeKey, originalKey] = key.split(KEY_SEPARATOR);
    const [channel, accuracyType, component] = originalKey.split('-');
    const headings = this.getSortedKeySortedHeadingMap(this.reportData.sortedKeys, this.reportData.sortedHeadings);
    let label = `${this.getLabelPrefix(compositeKey)}${headings[accuracyType]}-${component}(${channel})`;
    return key.includes(GOAL_KEY) ? `${GOAL_KEY}-${label}` : `${label}`;
  };

  getLabelPrefix = (compositeKey) => {
    let prefix = '';
    if (this.props.shownSnapshots.length > 1) {
      prefix = `[${extractFromCompositeKey(compositeKey, SNAPSHOT_ID)}]`
    } else if (this.reportData.aggregatedFields.length && this.reportData.aggregatedFields.includes(MARKETPLACE)) {
      const marketplace = this.props.marketplaceOptions.filter((mp) => mp.id === extractFromCompositeKey(compositeKey, MARKETPLACE))[0];
      prefix = `[${marketplace.label}]`;
    }
    return prefix;
  }

  onToggleChart = () => {
    this.setState({ displayedChart: this.state.displayedChart === CHART_TYPE.LINE ? CHART_TYPE.BAR : CHART_TYPE.LINE });
  };

  getSnapshotDropdownOptions = () => {
    const { snapshots } = this.props;
    return snapshots.map((snapshot, index) => {
      return { label: prepareSnapshotName(snapshot, this.props.appConfig.liveSnapshotId), id: index }
    });
  };

  getChannelDropdownOptions = (allChannels) => { return allChannels.map((channel) => { return { label: channel, id: channel } }) };

  getComponentDropdownOptions = (allComponents) => { return allComponents.map((component) => { return { label: component, id: component } }) };

  getAccuracyVarianceDropdownOptions = () => {
    let map = this.getSortedKeySortedHeadingMap(this.reportData.sortedKeys, this.reportData.sortedHeadings);
    return COMPONENT_GRAPH_ACCURACY_OPTION_KEYS.map((key) => { return { label: map[key], id: key } })
  };

  getAggregatedDropdownOptions = (aggregatedFields) => {
    return aggregatedFields.map(field => {
      const fieldValues = extractFromCompositeKeys(this.reportData.compositeKeys, field);
      return this.props[`${field}Options`].filter(op => fieldValues.includes(op.id));
    }).flat();
  }

  getSortedKeySortedHeadingMap = (keys, headings) => {
    let map = {};
    keys.forEach((key, i) => (map[key] = headings[i]));
    return map;
  };

  updateShownAccuracyVariance = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownComponentAccuracyKeys');
  };

  updateShownComponentChannel = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownComponentChannels');
  };

  updateShownComponents = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownComponents');
  };

  updateShownComponentSnapshots = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownComponentSnapshots');
  };

  updateShownComponentAggregates = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownComponentAggregatedItems');
  };

  reformatRecordData = (data) => {
    return parseFloat(data.toFixed(2));
  };

  getTrendGoal = (id) => {
    for (const [key, goal] of Object.entries(this.props.trendGoals)) {
      if (key.includes(id)) {
        return goal;
      }
    }
    return 0;
  };

  parseData = () => {
    const { requestPayloads, responsePayloads, liveSnapshot } = this.props;

    const reportData = this.parsePayloadData(requestPayloads, responsePayloads, liveSnapshot);

    return reportData;
  };

  parsePayloadData = (requestPayloads, responsePayloads, liveSnapshot) => {
    const aggregatedFields = getNonAggregatedFieldsWithMultiValues(requestPayloads, [REPORT_REQUEST_PAYLOAD.MARKETPLACE]);

    const dataSetKeys = [SNAPSHOT_ID].concat(aggregatedFields || []);

    let compositeKeys = getCompositeKeys(replaceLiveSnapshotId(requestPayloads, liveSnapshot), dataSetKeys);
    const transformedData = trendDataWithCompositeKeys(responsePayloads, compositeKeys, this.reformatRecordData, this.getTrendGoal);

    return { ...transformedData, compositeKeys, aggregatedFields };
  };

  getShownIds = () => {
    let ids = [];
    this.props.shownChannels.map((channel) => {
      this.props.shownAccuracyKeys.map((accuracyType) => {
        this.props.shownComponents.map((component) => {
          this.props.shownSnapshots.map((snapshotIndex) => {
            if (this.reportData.aggregatedFields.length) {
              this.props.shownAggregatedItems.map((item) => {
                ids.push(...this.getReportIds(searchCompositeKey(item, this.reportData.compositeKeys, this.reportData.aggregatedFields[0]), channel, accuracyType, component));
              })
            } else {
              ids.push(...this.getReportIds(this.reportData.compositeKeys[snapshotIndex], channel, accuracyType, component));
            }
          });
        });
      });
    });
    return ids;
  };

  getReportIds = (compositeKey, channel, accuracyType, component) => {
    let ids = [];
    let key = compositeKey + KEY_SEPARATOR + [channel, accuracyType, component].join('-');
    ids.push(key);
    if (accuracyType.includes('dailyCPAccuracyAV')) {
      ids.push([key, GOAL_KEY].join('-'));
    }
    return ids;
  }

  getDropdownOptions = (allChannels, allComponents, aggregatedFields) => {
    this.accuracyOptions = this.getAccuracyVarianceDropdownOptions();
    this.channelOptions = this.getChannelDropdownOptions(allChannels);
    this.componentOptions = this.getComponentDropdownOptions(allComponents);
    this.snapshotOptions = this.getSnapshotDropdownOptions();
    this.aggregatedOptions = this.getAggregatedDropdownOptions(aggregatedFields);
  };

  onDownloadDisplayedData = () => {
    this.props.downloadDisplayedData(this.data, this.allLinesId, `Component Trend Data_${new Date(Date.now()).toLocaleString()}.xls`, this.formatChartLabel)
  }

  processHeadings = () => {
    const commonHeadings = ["Region", "Channel", "Marketplace", "Month", "Component", "Component Type"];
    const sortedHeadings = this.reportData.sortedHeadings;
    const sortedCommonHeadings = sortedHeadings.filter(heading => commonHeadings.includes(heading));
    let headings = [].concat(sortedCommonHeadings);
    for (let i = sortedCommonHeadings.length; i < sortedHeadings.length; i++) {
      if (this.reportData.aggregatedFields.length && this.reportData.aggregatedFields.includes(MARKETPLACE)) {
        const marketplcaes = this.reportData.compositeKeys.map((key) => {
          const marketplace = this.props.marketplaceOptions.filter((mp) => mp.id === extractFromCompositeKey(key, MARKETPLACE))[0];
          return `[${marketplace.label}]${sortedHeadings[i]}`;
        })
        headings.push(...marketplcaes);
      } else {
        const snapshots = this.props.snapshots.map(snapshot => {
          return `[${prepareSnapshotName(snapshot, this.props.appConfig.liveSnapshotId)}]${sortedHeadings[i]}`
        });
        headings.push(...snapshots);
      }
    }
    return headings;
  };

  getPrepareDownloadData = () => {
    const loadedAccuracyData = this.props.responsePayloads;
    const keys = loadedAccuracyData[0].sortedKeys;
    const commonKeys = keys.filter(key => ["region", "channel", "marketplace", "month", "component", "type"].includes(key));
    return [keys, commonKeys];
  }

  onDownloadData = () => {
    let array = [];
    const loadedAccuracyData = this.props.responsePayloads;
    const [keys, commonKeys] = this.getPrepareDownloadData();
    const dataSortedByCountOfMonths = loadedAccuracyData.sort((a, b) => b.sortedMonths.length - a.sortedMonths.length);
    const headings = this.props.snapshots.length > 1 || this.reportData.aggregatedFields.length ? this.processHeadings() : loadedAccuracyData[0].sortedHeadings;
    array.push(headings.join('\t'));
    for (let monthIndex = 0; monthIndex < dataSortedByCountOfMonths[0].sortedMonths.length; monthIndex++) {
      for (let accuracyRecordIndex = 0; accuracyRecordIndex < dataSortedByCountOfMonths[0].accuracyTrend[monthIndex].length; accuracyRecordIndex++) {
        let rowArray = commonKeys.map(key => dataSortedByCountOfMonths[0].accuracyTrend[monthIndex][accuracyRecordIndex][key]);
        let month = dataSortedByCountOfMonths[0].accuracyTrend[monthIndex][accuracyRecordIndex].month;
        const filteredRows = keys.filter(recordKey => !commonKeys.includes(recordKey))
          .map(recordKey => {
            return dataSortedByCountOfMonths.map(accuracyData => {
            if (accuracyData.sortedMonths.includes(month)) {
              let monthIndexForCurrentSnapshot = accuracyData.sortedMonths.indexOf(month);
              return accuracyData.accuracyTrend[monthIndexForCurrentSnapshot][accuracyRecordIndex][recordKey]
            } else {
              return ""
                }
              })
          }).flat();
        rowArray.push(...filteredRows);
        array.push(rowArray.join('\t'));
      }
    }
    this.downloadFile(array);
  };

  onDownloadDataByRow = () => {
    let array = [];
    const [keys, commonKeys] = this.getPrepareDownloadData();
    const headings = this.reportData.sortedHeadings;
    array.push(headings.join('\t'));
    this.props.responsePayloads.map((payload) => {
      for (let monthIndex = 0; monthIndex < payload.sortedMonths.length; monthIndex++) {
        for (let accuracyRecordIndex = 0; accuracyRecordIndex < payload.accuracyTrend[monthIndex].length; accuracyRecordIndex++) {
          let rowArray = commonKeys.map(key => payload.accuracyTrend[monthIndex][accuracyRecordIndex][key]);
          let records = keys.filter(recordKey => !commonKeys.includes(recordKey))
            .map(recordKey => payload.accuracyTrend[monthIndex][accuracyRecordIndex][recordKey]);
          rowArray.push(...records);
          array.push(rowArray.join('\t'));
        }
      }
    })
    this.downloadFile(array);
  }

  downloadFile = (dataArray) => {
    this.props.downloadFile(dataArray.join('\n'), DOWNLOAD_FILE_NAME);
    this.props.onDownloadDataSuccess();
    this.props.resetButtons();
  }

  onDownloadDataWindowCancel = () => {
    this.setState({ downloadDataWindowVisible: false });
  };

  onDownloadDataWindowSubmit = (downloadType) => {
    this.setState({ downloadDataWindowVisible: false });
    if (downloadType === 'rows') {
      this.onDownloadDataByRow();
    } else {
      this.onDownloadData();
    }
  };

  render() {
    if (!this.reportData && this.props.responsePayloads.length > 0) {
      this.reportData = this.parseData();
      this.props.updateReportData(this.reportData);
      const { allChannels, allComponents, aggregatedFields } = this.reportData;
      this.getDropdownOptions(allChannels, allComponents, aggregatedFields);
      const aggregateDefaultOptions = aggregatedFields.length ? extractFromCompositeKeys(this.reportData.compositeKeys, aggregatedFields[0]) : [];
      this.props.setDefaultData(allChannels, this.props.snapshots.length, aggregateDefaultOptions);
    }
    if (this.reportData) {
      this.data = this.props.getDataForSharedMonths(this.reportData.processedComponentData, this.props.shownSnapshots);
      this.allLinesId = this.getShownIds();
    }

    const aggregatedField = this.reportData.aggregatedFields[0];
    const { downloadDataWindowVisible, displayedChart } = this.state;
    const { title, bothAccuracyAndVarianceSelected, shownComponents, shownSnapshots, shownAggregatedItems, shownAccuracyKeys, shownChannels } = this.props;

    return (
      <div key={2} className="awsui-util-container">
        {downloadDataWindowVisible && (
          <DownloadReportDataWindow
            onCancel={this.onDownloadDataWindowCancel}
            onSubmit={this.onDownloadDataWindowSubmit}
          />
        )}
        <TrendReportChartTitle
          text={title}
          allLinesId={this.allLinesId}
          chartType={displayedChart}
          onChangeFn={this.onToggleChart}
          onDownloadDisplayedData={this.onDownloadDisplayedData}
          data={this.data}
        />
        <ColumnLayout columns={4}>
          <div data-awsui-column-layout-root="true">
            <div>
              <TrendFilter
                text="Channel"
                classname={AccuracyButtonNewStyle}
                options={this.channelOptions}
                onChange={this.updateShownComponentChannel}
                selectedIds={shownChannels}
              />
            </div>
            <div>
              <TrendFilter
                text="Accuracy/Per Unit Variance"
                classname={AccuracyButtonNewStyle}
                options={this.accuracyOptions}
                onChange={this.updateShownAccuracyVariance}
                selectedIds={shownAccuracyKeys}
              />
            </div>
            <div>
              <TrendFilter
                text="Component"
                classname={AccuracyButtonNewStyle}
                options={this.componentOptions}
                onChange={this.updateShownComponents}
                selectedIds={shownComponents}
              />
            </div>
            {aggregatedField && aggregatedField.includes(MARKETPLACE) && (
              <div>
                <TrendFilter
                  text="Marketplaces"
                  classname={AccuracyButtonNewStyle}
                  options={this.aggregatedOptions}
                  onChange={this.updateShownComponentAggregates}
                  selectedIds={shownAggregatedItems}
                />
              </div>)
            }
            {!aggregatedField && (
              <div>
                <TrendFilter
                  text="Snapshots"
                  classname={AccuracyButtonNewStyle}
                  options={this.snapshotOptions}
                  onChange={this.updateShownComponentSnapshots}
                  selectedIds={shownSnapshots}
                />
              </div>)
            }
          </div>
        </ColumnLayout>
        <TrendReportChart
          chartType={displayedChart}
          allLinesId={this.allLinesId}
          labelFormatter={this.formatChartLabel}
          data={this.data}
          bothAccuracyAndVarianceSelected={bothAccuracyAndVarianceSelected(shownComponents)}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/components/trend_component.js